// Google Calendar-style Module — vistas día/semana/mes/año/agenda, drag-create,
// mini-calendario, búsqueda, atajos de teclado, multi-día, colores, recordatorios.
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Plus, Mic, MicOff, Clock, MapPin, Bell,
  ChevronLeft, ChevronRight, Share2, Trash2, X, Search,
  CalendarDays, List, Columns, Grid3x3, Menu, Edit2, Users, Video, Sparkles, Loader2
} from 'lucide-react';
import ShareModal from './ShareModal';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MONTH_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const DAY_NAMES_SHORT = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const DAY_NAMES_LONG = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const PRIORITY_COLORS = { urgent:'#ef4444', high:'#f97316', normal:'#3b82f6', low:'#6b7280' };
const PRIORITY_LABELS = { urgent:'Urgente', high:'Alta', normal:'Normal', low:'Baja' };
const EVENT_COLORS = [
  { name: 'Tomate',    value: '#d50000' },
  { name: 'Mandarina', value: '#f4511e' },
  { name: 'Plátano',   value: '#f6bf26' },
  { name: 'Salvia',    value: '#33b679' },
  { name: 'Albahaca',  value: '#0b8043' },
  { name: 'Pavo real', value: '#039be5' },
  { name: 'Arándano',  value: '#3f51b5' },
  { name: 'Lavanda',   value: '#7986cb' },
  { name: 'Uva',       value: '#8e24aa' },
  { name: 'Flamenco',  value: '#e67c73' },
  { name: 'Grafito',   value: '#616161' },
];

const DEFAULT_EVENT = {
  title: '', description: '', start_datetime: '', end_datetime: '',
  location: '', reminder_minutes: 30, is_all_day: false, priority: 'normal',
  color: '#039be5', meeting_link: '', guests: ''
};

// ── Utilidades de fecha ─────────────────────────────────────────────────────
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; };
const sameDay    = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const toLocalDT  = (d) => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtTime    = (d) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
const fmtDate    = (d) => d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const CalendarModule = ({ user }) => {
  const [events, setEvents]                 = useState([]);
  const [currentDate, setCurrentDate]       = useState(new Date());
  const [view, setView]                     = useState('month'); // day | week | month | year | agenda
  const [selectedEvent, setSelectedEvent]   = useState(null);
  const [showCreate, setShowCreate]         = useState(false);
  const [editingEvent, setEditingEvent]     = useState(null);
  const [listening, setListening]           = useState(false);
  const [shareModal, setShareModal]         = useState({ open: false, event: null });
  const [newEvent, setNewEvent]             = useState(DEFAULT_EVENT);
  const [search, setSearch]                 = useState('');
  const [showSidebar, setShowSidebar]       = useState(true);
  const [miniMonth, setMiniMonth]           = useState(new Date());
  const [jarvisOpen, setJarvisOpen]         = useState(false);
  const [jarvisInput, setJarvisInput]       = useState('');
  const [jarvisLoading, setJarvisLoading]   = useState(false);
  const [jarvisReply, setJarvisReply]       = useState('');
  const recognitionRef = useRef(null);
  const gridRef        = useRef(null);

  // ── Cargar eventos del rango visible ──────────────────────────────────────
  const visibleRange = useMemo(() => {
    if (view === 'day') return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    if (view === 'week') {
      const s = startOfWeek(currentDate);
      return { start: s, end: endOfDay(addDays(s, 6)) };
    }
    if (view === 'year') {
      return { start: new Date(currentDate.getFullYear(), 0, 1), end: new Date(currentDate.getFullYear(), 11, 31, 23, 59, 59) };
    }
    if (view === 'agenda') {
      return { start: startOfDay(currentDate), end: endOfDay(addDays(currentDate, 60)) };
    }
    // month
    const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const last  = new Date(currentDate.getFullYear(), currentDate.getMonth()+1, 0, 23, 59, 59);
    return { start: addDays(first, -first.getDay()), end: addDays(last, 6 - last.getDay()) };
  }, [currentDate, view]);

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/calendar/events/v2/${user.id}?start_date=${visibleRange.start.toISOString()}&end_date=${visibleRange.end.toISOString()}`
      );
      setEvents(res.data || []);
    } catch (e) { /* silent */ }
  }, [user.id, visibleRange.start, visibleRange.end]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ── Voice ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.onresult = (e) => {
      setNewEvent(p => ({ ...p, title: e.results[0][0].transcript }));
      setListening(false);
      setShowCreate(true);
    };
    r.onerror = r.onend = () => setListening(false);
    recognitionRef.current = r;
  }, []);

  const handleVoice = () => {
    if (!recognitionRef.current) { toast.error('Voz no disponible'); return; }
    if (listening) { recognitionRef.current.stop(); return; }
    recognitionRef.current.start();
    setListening(true);
  };

  // ── Atajos de teclado al estilo Google Calendar ───────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || showCreate) return;
      if (e.key === 'd' || e.key === '1') setView('day');
      else if (e.key === 'w' || e.key === '2') setView('week');
      else if (e.key === 'm' || e.key === '3') setView('month');
      else if (e.key === 'y' || e.key === '4') setView('year');
      else if (e.key === 'a' || e.key === '5') setView('agenda');
      else if (e.key === 't') setCurrentDate(new Date());
      else if (e.key === 'c') openCreate();
      else if (e.key === '/') { e.preventDefault(); document.getElementById('cal-search')?.focus(); }
      else if (e.key === 'ArrowLeft' || e.key === 'k') navigate(-1);
      else if (e.key === 'ArrowRight' || e.key === 'j') navigate(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, showCreate, currentDate]);

  // ── Navegación ─────────────────────────────────────────────────────────────
  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'day')   d.setDate(d.getDate() + dir);
    if (view === 'week')  d.setDate(d.getDate() + 7 * dir);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    if (view === 'year')  d.setFullYear(d.getFullYear() + dir);
    if (view === 'agenda')d.setDate(d.getDate() + 7 * dir);
    setCurrentDate(d);
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const openCreate = (slotDate) => {
    const start = slotDate || new Date(Math.ceil(Date.now()/3600000)*3600000);
    const end   = new Date(start.getTime() + 60*60*1000);
    setEditingEvent(null);
    setNewEvent({ ...DEFAULT_EVENT, start_datetime: toLocalDT(start), end_datetime: toLocalDT(end) });
    setShowCreate(true);
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setNewEvent({
      title: ev.title || '',
      description: ev.description || '',
      start_datetime: ev.start_datetime?.slice(0,16) || '',
      end_datetime: ev.end_datetime?.slice(0,16) || '',
      location: ev.location || '',
      reminder_minutes: ev.reminder_minutes ?? 30,
      is_all_day: !!ev.is_all_day,
      priority: ev.priority || 'normal',
      color: ev.color || '#039be5',
      meeting_link: ev.meeting_link || '',
      guests: ev.guests || ''
    });
    setSelectedEvent(null);
    setShowCreate(true);
  };

  const saveEvent = async () => {
    if (!newEvent.title || !newEvent.start_datetime) { toast.error('Completa título y fecha'); return; }
    try {
      if (editingEvent) {
        await axios.put(`${API}/calendar/events/${editingEvent.id}`, newEvent);
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/calendar/events/v2?user_id=${user.id}`, { ...newEvent, is_voice_created: false });
        toast.success('Evento creado');
      }
      setShowCreate(false);
      setEditingEvent(null);
      setNewEvent(DEFAULT_EVENT);
      loadEvents();
    } catch { toast.error(editingEvent ? 'Error al actualizar' : 'Error al crear'); }
  };

  // ── Jarvis: crear / consultar eventos en lenguaje natural ─────────────────
  const askJarvis = async () => {
    const text = jarvisInput.trim();
    if (!text) return;
    setJarvisLoading(true);
    setJarvisReply('');
    try {
      const now = new Date();
      const tzNote = `(Hoy es ${now.toLocaleDateString('es-MX', { weekday:'long', day:'numeric', month:'long', year:'numeric' })} ${fmtTime(now)} hora de México). user_id: ${user.id}.`;
      const message = `${text}\n\n${tzNote}\nSi creas, actualizas o consultas eventos, usa las herramientas MCP create_event/list_events/update_event con user_id="${user.id}" y formato ISO 8601 para fechas. Confirma con datos reales después de ejecutar.`;
      const res = await axios.post(`${API}/chat/send`, {
        user_id: user.id,
        message,
        agent_type: 'general',
      }, { timeout: 60000 });
      setJarvisReply(res.data?.response || 'Listo.');
      toast.success('Jarvis procesó tu solicitud');
      // Refrescar eventos por si creó/actualizó
      setTimeout(loadEvents, 500);
    } catch (e) {
      setJarvisReply('No pude completar la solicitud. Intenta de nuevo.');
      toast.error('Error al consultar a Jarvis');
    } finally {
      setJarvisLoading(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await axios.delete(`${API}/calendar/events/${id}`);
      toast.success('Eliminado');
      setSelectedEvent(null);
      loadEvents();
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Filtrado / búsqueda ───────────────────────────────────────────────────
  const filteredEvents = useMemo(() => {
    if (!search.trim()) return events;
    const q = search.toLowerCase();
    return events.filter(e =>
      e.title?.toLowerCase().includes(q) ||
      e.description?.toLowerCase().includes(q) ||
      e.location?.toLowerCase().includes(q)
    );
  }, [events, search]);

  const eventsForDay = (date) => filteredEvents.filter(e => {
    if (!e.start_datetime) return false;
    const ed = new Date(e.start_datetime);
    return sameDay(ed, date);
  });

  // ── Header / Title ────────────────────────────────────────────────────────
  const headerTitle = useMemo(() => {
    if (view === 'day')   return fmtDate(currentDate);
    if (view === 'week') {
      const s = startOfWeek(currentDate); const e = addDays(s, 6);
      if (s.getMonth() === e.getMonth()) return `${s.getDate()} – ${e.getDate()} ${MONTH_NAMES[s.getMonth()]} ${s.getFullYear()}`;
      return `${s.getDate()} ${MONTH_SHORT[s.getMonth()]} – ${e.getDate()} ${MONTH_SHORT[e.getMonth()]} ${e.getFullYear()}`;
    }
    if (view === 'year')  return String(currentDate.getFullYear());
    if (view === 'agenda')return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }, [currentDate, view]);

  // ────────────────────────────────────────────────────────────────────────────
  // VISTA MES
  // ────────────────────────────────────────────────────────────────────────────
  const MonthView = () => {
    const days = [];
    for (let d = new Date(visibleRange.start); d <= visibleRange.end; d = addDays(d, 1)) days.push(new Date(d));
    return (
      <div className="flex-1 flex flex-col bg-white rounded-xl overflow-hidden border border-slate-200">
        {/* Header de días */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {DAY_NAMES_SHORT.map(d => (
            <div key={d} className="px-2 py-2 text-[11px] font-semibold uppercase text-slate-500 text-center tracking-wider">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-[500px]">
          {days.map((d, i) => {
            const isCurrentMonth = d.getMonth() === currentDate.getMonth();
            const isToday = sameDay(d, new Date());
            const dayEvents = eventsForDay(d);
            return (
              <div key={i}
                   onClick={() => { const dt = new Date(d); dt.setHours(9,0); openCreate(dt); }}
                   className={`border-r border-b border-slate-200 p-1.5 min-h-[90px] cursor-pointer hover:bg-slate-50 transition-colors ${!isCurrentMonth ? 'bg-slate-50' : ''}`}>
                <div className="flex justify-end mb-1">
                  <span className={`text-xs font-medium ${
                    isToday ? 'bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
                            : isCurrentMonth ? 'text-slate-700' : 'text-slate-400'
                  }`}>{d.getDate()}</span>
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map(ev => (
                    <button key={ev.id}
                      onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                      className="w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate hover:opacity-80"
                      style={{ background: `${ev.color || '#039be5'}33`, color: ev.color || '#039be5', borderLeft: `3px solid ${ev.color || '#039be5'}` }}>
                      {!ev.is_all_day && <span className="opacity-70 mr-1">{fmtTime(new Date(ev.start_datetime))}</span>}
                      {ev.title}
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <button onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(d)); setView('day'); }}
                            className="text-[10px] text-slate-500 hover:text-indigo-600 px-1.5">
                      +{dayEvents.length - 3} más
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // VISTA SEMANA / DÍA (time grid)
  // ────────────────────────────────────────────────────────────────────────────
  const TimeGridView = ({ days }) => {
    const HOUR_HEIGHT = 48; // px

    const positionedEvents = (date) => {
      return eventsForDay(date).filter(e => !e.is_all_day).map(e => {
        const start = new Date(e.start_datetime);
        const end   = e.end_datetime ? new Date(e.end_datetime) : new Date(start.getTime() + 60*60*1000);
        const startMin = start.getHours()*60 + start.getMinutes();
        const durMin   = Math.max(30, (end - start) / 60000);
        return { ...e, top: (startMin/60)*HOUR_HEIGHT, height: (durMin/60)*HOUR_HEIGHT };
      });
    };

    const allDay = (date) => eventsForDay(date).filter(e => e.is_all_day);

    const handleSlotClick = (date, hour, e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const minutes = Math.floor((offsetY / HOUR_HEIGHT) * 60 / 15) * 15;
      const dt = new Date(date); dt.setHours(hour, minutes, 0, 0);
      openCreate(dt);
    };

    const now = new Date();
    const nowMin = now.getHours()*60 + now.getMinutes();

    return (
      <div className="flex-1 flex flex-col bg-white rounded-xl overflow-hidden border border-slate-200">
        {/* Cabecera con días */}
        <div className="flex border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="w-14 flex-shrink-0" />
          {days.map((d, i) => {
            const isToday = sameDay(d, now);
            return (
              <div key={i} className="flex-1 text-center py-2 border-l border-slate-200">
                <p className="text-[10px] uppercase text-slate-500 tracking-wider">{DAY_NAMES_SHORT[d.getDay()]}</p>
                <p className={`text-lg font-semibold mt-0.5 ${isToday ? 'inline-flex items-center justify-center w-9 h-9 rounded-full bg-indigo-600 text-white' : 'text-slate-800'}`}>
                  {d.getDate()}
                </p>
              </div>
            );
          })}
        </div>

        {/* Sección "todo el día" */}
        <div className="flex border-b border-slate-200 min-h-[34px]">
          <div className="w-14 flex-shrink-0 text-[10px] text-slate-400 px-2 py-1 text-right">Todo el día</div>
          {days.map((d, i) => (
            <div key={i} className="flex-1 border-l border-slate-200 p-1 space-y-0.5">
              {allDay(d).map(ev => (
                <button key={ev.id} onClick={() => setSelectedEvent(ev)}
                  className="w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate text-white"
                  style={{ background: ev.color || '#039be5' }}>
                  {ev.title}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Grid de horas */}
        <div ref={gridRef} className="flex-1 overflow-y-auto custom-scrollbar relative" style={{ maxHeight: 'calc(100vh - 320px)' }}>
          <div className="flex" style={{ height: HOUR_HEIGHT * 24 }}>
            {/* Columna de horas */}
            <div className="w-14 flex-shrink-0">
              {HOURS.map(h => (
                <div key={h} className="text-[10px] text-slate-400 text-right pr-2 border-t border-slate-200"
                     style={{ height: HOUR_HEIGHT }}>
                  {h === 0 ? '' : `${String(h).padStart(2,'0')}:00`}
                </div>
              ))}
            </div>
            {/* Columnas de días */}
            {days.map((d, di) => (
              <div key={di} className="flex-1 border-l border-slate-200 relative">
                {HOURS.map(h => (
                  <div key={h}
                       onClick={(e) => handleSlotClick(d, h, e)}
                       className="border-t border-slate-200 hover:bg-indigo-50 cursor-pointer"
                       style={{ height: HOUR_HEIGHT }} />
                ))}
                {/* Eventos absolutos */}
                {positionedEvents(d).map(ev => (
                  <button key={ev.id}
                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                    className="absolute left-1 right-1 rounded px-1.5 py-1 text-left overflow-hidden hover:opacity-90 hover:shadow-lg transition-all"
                    style={{
                      top: ev.top, height: ev.height,
                      background: ev.color || '#039be5',
                      color: '#fff',
                      borderLeft: '3px solid rgba(0,0,0,0.25)'
                    }}>
                    <p className="text-[10px] font-semibold truncate">{ev.title}</p>
                    <p className="text-[9px] opacity-80 truncate">
                      {fmtTime(new Date(ev.start_datetime))}
                      {ev.end_datetime && ` – ${fmtTime(new Date(ev.end_datetime))}`}
                    </p>
                    {ev.location && <p className="text-[9px] opacity-70 truncate">📍 {ev.location}</p>}
                  </button>
                ))}
                {/* Línea "ahora" */}
                {sameDay(d, now) && (
                  <div className="absolute left-0 right-0 z-20 pointer-events-none"
                       style={{ top: (nowMin/60) * HOUR_HEIGHT }}>
                    <div className="w-full h-[2px] bg-red-500" />
                    <div className="absolute -left-1 -top-[5px] w-3 h-3 bg-red-500 rounded-full" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const WeekView = () => {
    const start = startOfWeek(currentDate);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return <TimeGridView days={days} />;
  };
  const DayView = () => <TimeGridView days={[currentDate]} />;

  // ────────────────────────────────────────────────────────────────────────────
  // VISTA AGENDA (lista cronológica)
  // ────────────────────────────────────────────────────────────────────────────
  const AgendaView = () => {
    const sorted = [...filteredEvents].sort((a,b) => new Date(a.start_datetime) - new Date(b.start_datetime));
    const grouped = sorted.reduce((acc, ev) => {
      const key = ev.start_datetime?.slice(0,10) || 'no-date';
      (acc[key] = acc[key] || []).push(ev);
      return acc;
    }, {});
    return (
      <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-y-auto custom-scrollbar p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 240px)' }}>
        {Object.keys(grouped).length === 0 && (
          <p className="text-center text-slate-500 py-12">No hay eventos próximos</p>
        )}
        {Object.entries(grouped).map(([key, evs]) => {
          const d = new Date(key);
          const isToday = sameDay(d, new Date());
          return (
            <div key={key}>
              <div className="flex items-center gap-3 mb-2 sticky top-0 bg-white py-2 z-10">
                <div className={`w-12 text-center ${isToday ? 'text-indigo-600' : 'text-slate-500'}`}>
                  <p className="text-[10px] uppercase tracking-wider">{DAY_NAMES_SHORT[d.getDay()]}</p>
                  <p className={`text-2xl font-bold ${isToday ? 'inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600 text-white' : ''}`}>{d.getDate()}</p>
                </div>
                <p className="text-sm text-slate-500">{MONTH_NAMES[d.getMonth()]} {d.getFullYear()}</p>
              </div>
              <div className="space-y-1.5 ml-16">
                {evs.map(ev => (
                  <button key={ev.id} onClick={() => setSelectedEvent(ev)}
                    className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 border-l-4 transition-colors flex items-center gap-3"
                    style={{ borderLeftColor: ev.color || '#039be5' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-900 font-medium truncate">{ev.title}</p>
                      <p className="text-slate-500 text-xs flex items-center gap-2 mt-0.5">
                        {!ev.is_all_day && <><Clock size={10} /> {fmtTime(new Date(ev.start_datetime))}</>}
                        {ev.location && <><MapPin size={10} /> {ev.location}</>}
                      </p>
                    </div>
                    <div className="w-2 h-2 rounded-full" style={{ background: ev.color || '#039be5' }} />
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // VISTA AÑO
  // ────────────────────────────────────────────────────────────────────────────
  const YearView = () => {
    const year = currentDate.getFullYear();
    return (
      <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 overflow-y-auto custom-scrollbar p-2" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        {MONTH_NAMES.map((m, idx) => {
          const first = new Date(year, idx, 1);
          const lastD = new Date(year, idx+1, 0).getDate();
          const offset = first.getDay();
          const cells = [];
          for (let i = 0; i < offset; i++) cells.push(null);
          for (let i = 1; i <= lastD; i++) cells.push(i);
          return (
            <div key={idx} className="bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-300 transition-colors cursor-pointer"
                 onClick={() => { setCurrentDate(new Date(year, idx, 1)); setView('month'); }}>
              <p className="text-sm font-semibold text-slate-900 mb-2">{m}</p>
              <div className="grid grid-cols-7 gap-0.5 text-center">
                {DAY_NAMES_SHORT.map(d => <p key={d} className="text-[8px] text-slate-400">{d[0]}</p>)}
                {cells.map((day, i) => {
                  if (!day) return <div key={i} />;
                  const d = new Date(year, idx, day);
                  const isToday = sameDay(d, new Date());
                  const has = events.some(e => e.start_datetime?.startsWith(`${year}-${String(idx+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`));
                  return (
                    <div key={i} className={`text-[10px] py-0.5 rounded-full ${isToday ? 'bg-indigo-600 text-white' : has ? 'text-indigo-600 font-bold' : 'text-slate-500'}`}>
                      {day}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // MINI CALENDARIO (sidebar)
  // ────────────────────────────────────────────────────────────────────────────
  const MiniCalendar = () => {
    const y = miniMonth.getFullYear(), m = miniMonth.getMonth();
    const first = new Date(y, m, 1).getDay();
    const lastDay = new Date(y, m+1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let i = 1; i <= lastDay; i++) cells.push(i);
    return (
      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-900">{MONTH_NAMES[m]} {y}</p>
          <div className="flex gap-1">
            <button onClick={() => setMiniMonth(new Date(y, m-1, 1))} className="p-1 hover:bg-slate-100 rounded"><ChevronLeft size={12} className="text-slate-500" /></button>
            <button onClick={() => setMiniMonth(new Date(y, m+1, 1))} className="p-1 hover:bg-slate-100 rounded"><ChevronRight size={12} className="text-slate-500" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {DAY_NAMES_SHORT.map(d => <p key={d} className="text-[9px] text-slate-400 py-1">{d[0]}</p>)}
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const d = new Date(y, m, day);
            const isToday = sameDay(d, new Date());
            const isSelected = sameDay(d, currentDate);
            const has = events.some(e => e.start_datetime?.startsWith(`${y}-${String(m+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`));
            return (
              <button key={i} onClick={() => setCurrentDate(d)}
                className={`text-[10px] py-1 rounded-full transition-colors relative
                  ${isToday ? 'bg-indigo-600 text-white font-bold'
                  : isSelected ? 'bg-indigo-100 text-indigo-700'
                  : 'text-slate-700 hover:bg-slate-100'}`}>
                {day}
                {has && !isToday && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-400" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <SectionAgent section="calendar" />
      <div className="p-3 md:p-5 h-full flex flex-col">
        {/* Toolbar superior estilo Google */}
        <div className="flex items-center gap-2 md:gap-3 mb-4 flex-wrap">
          <button onClick={() => setShowSidebar(s => !s)} className="p-2 hover:bg-slate-100 rounded-lg lg:hidden" title="Menú">
            <Menu size={18} className="text-slate-500" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="text-indigo-600" size={22} />
            <h1 className="text-lg md:text-xl font-bold text-slate-900 hidden sm:block">Calendario</h1>
          </div>
          <button onClick={() => setCurrentDate(new Date())}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium">
            Hoy
          </button>
          <div className="flex">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><ChevronLeft size={18} className="text-slate-500" /></button>
            <button onClick={() => navigate(1)}  className="p-1.5 hover:bg-slate-100 rounded-lg"><ChevronRight size={18} className="text-slate-500" /></button>
          </div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900 truncate flex-1">{headerTitle}</h2>

          {/* Búsqueda */}
          <div className="relative hidden md:block">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input id="cal-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar (/)"
              className="bg-slate-100 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-900 w-44 focus:outline-none focus:border-indigo-500" />
          </div>

          {/* Selector de vista */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {[
              { id: 'day',    label: 'Día',    icon: Columns },
              { id: 'week',   label: 'Semana', icon: CalendarDays },
              { id: 'month',  label: 'Mes',    icon: Grid3x3 },
              { id: 'year',   label: 'Año',    icon: Calendar },
              { id: 'agenda', label: 'Agenda', icon: List },
            ].map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                className={`px-2.5 md:px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1
                  ${view === v.id ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                <v.icon size={13} />
                <span className="hidden md:inline">{v.label}</span>
              </button>
            ))}
          </div>

          <button onClick={handleVoice}
            className={`p-2 rounded-lg transition-colors ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}`}>
            {listening ? <MicOff size={16} /> : <Mic size={16} />}
          </button>

          <button onClick={() => { setJarvisOpen(true); setJarvisReply(''); setJarvisInput(''); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-semibold transition-colors shadow"
            title="Crea eventos con lenguaje natural usando Jarvis">
            <Sparkles size={16} /> <span className="hidden md:inline">Jarvis</span>
          </button>

          <button onClick={() => openCreate()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors">
            <Plus size={16} /> <span className="hidden md:inline">Crear</span>
          </button>
        </div>

        {/* Layout: sidebar + vista */}
        <div className="flex flex-1 gap-3 md:gap-4 min-h-0">
          {/* Sidebar */}
          <aside className={`w-56 flex-shrink-0 space-y-3 ${showSidebar ? 'block' : 'hidden'} lg:block`}>
            <button onClick={() => openCreate()}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm shadow-lg">
              <Plus size={16} /> Crear evento
            </button>
            <MiniCalendar />

            {/* Calendarios / categorías */}
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-semibold uppercase text-slate-500 tracking-wider mb-2">Mis calendarios</p>
              <div className="space-y-1.5">
                {EVENT_COLORS.slice(0,5).map(c => (
                  <div key={c.value} className="flex items-center gap-2 text-xs text-slate-700">
                    <div className="w-3.5 h-3.5 rounded" style={{ background: c.value }} />
                    <span>{c.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Atajos */}
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-[10px] text-slate-500 space-y-1">
              <p className="font-semibold text-slate-500 mb-1">Atajos</p>
              <p><kbd className="bg-slate-200 px-1 rounded">D</kbd>/<kbd className="bg-slate-200 px-1 rounded">W</kbd>/<kbd className="bg-slate-200 px-1 rounded">M</kbd> Vistas</p>
              <p><kbd className="bg-slate-200 px-1 rounded">T</kbd> Hoy · <kbd className="bg-slate-200 px-1 rounded">C</kbd> Crear</p>
              <p><kbd className="bg-slate-200 px-1 rounded">/</kbd> Buscar · <kbd className="bg-slate-200 px-1 rounded">←/→</kbd> Navegar</p>
            </div>
          </aside>

          {/* Vista activa */}
          <div className="flex-1 min-w-0 flex flex-col">
            {view === 'month'  && <MonthView />}
            {view === 'week'   && <WeekView />}
            {view === 'day'    && <DayView />}
            {view === 'year'   && <YearView />}
            {view === 'agenda' && <AgendaView />}
          </div>
        </div>
      </div>

      {/* Modal Crear/Editar evento */}
      {showCreate && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4" style={{ zIndex: 99999 }}
             onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-slate-900 font-semibold text-lg">{editingEvent ? 'Editar evento' : 'Nuevo evento'}</h3>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} className="text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <input value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))}
                placeholder="Añade un título" autoFocus
                className="w-full bg-transparent border-b-2 border-slate-200 focus:border-indigo-500 text-slate-900 text-xl py-2 px-1 focus:outline-none" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 mb-1 block">Inicio</label>
                  <input type="datetime-local" value={newEvent.start_datetime}
                    onChange={e => setNewEvent(p => ({ ...p, start_datetime: e.target.value }))}
                    className="input-glass w-full text-sm" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 mb-1 block">Fin</label>
                  <input type="datetime-local" value={newEvent.end_datetime}
                    onChange={e => setNewEvent(p => ({ ...p, end_datetime: e.target.value }))}
                    className="input-glass w-full text-sm" />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={newEvent.is_all_day}
                  onChange={e => setNewEvent(p => ({ ...p, is_all_day: e.target.checked }))} />
                Todo el día
              </label>

              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-slate-500" />
                <input value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))}
                  placeholder="Añadir ubicación" className="input-glass flex-1 text-sm" />
              </div>

              <div className="flex items-center gap-2">
                <Video size={16} className="text-slate-500" />
                <input value={newEvent.meeting_link} onChange={e => setNewEvent(p => ({ ...p, meeting_link: e.target.value }))}
                  placeholder="Link de videoconferencia (Zoom/Meet)" className="input-glass flex-1 text-sm" />
              </div>

              <div className="flex items-center gap-2">
                <Users size={16} className="text-slate-500" />
                <input value={newEvent.guests} onChange={e => setNewEvent(p => ({ ...p, guests: e.target.value }))}
                  placeholder="Invitados (correos separados por coma)" className="input-glass flex-1 text-sm" />
              </div>

              <div className="flex items-center gap-2">
                <Bell size={16} className="text-slate-500" />
                <select value={newEvent.reminder_minutes}
                  onChange={e => setNewEvent(p => ({ ...p, reminder_minutes: +e.target.value }))}
                  className="input-glass flex-1 text-sm">
                  <option value={0}>Sin recordatorio</option>
                  <option value={5}>5 minutos antes</option>
                  <option value={15}>15 minutos antes</option>
                  <option value={30}>30 minutos antes</option>
                  <option value={60}>1 hora antes</option>
                  <option value={1440}>1 día antes</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] text-slate-500 mb-1.5 block">Color</label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_COLORS.map(c => (
                    <button key={c.value} onClick={() => setNewEvent(p => ({ ...p, color: c.value }))}
                      title={c.name}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${newEvent.color === c.value ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ background: c.value }} />
                  ))}
                </div>
              </div>

              <textarea value={newEvent.description} onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))}
                placeholder="Añadir descripción"
                className="input-glass w-full text-sm resize-none" rows={3} />
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={saveEvent} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold">
                {editingEvent ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle evento */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4" style={{ zIndex: 99998 }}
             onClick={() => setSelectedEvent(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
               onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200" style={{ background: `linear-gradient(135deg, ${selectedEvent.color || '#039be5'}22, transparent)` }}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: selectedEvent.color || '#039be5' }} />
                  <h3 className="text-slate-900 font-semibold text-lg truncate">{selectedEvent.title}</h3>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(selectedEvent)} className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-slate-100 rounded-lg"><Edit2 size={15} /></button>
                  <button onClick={() => deleteEvent(selectedEvent.id)} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-100 rounded-lg"><Trash2 size={15} /></button>
                  <button onClick={() => setSelectedEvent(null)} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg"><X size={15} /></button>
                </div>
              </div>
              <div className="text-slate-500 text-sm flex items-center gap-1.5">
                <Clock size={13} />
                {new Date(selectedEvent.start_datetime).toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                {selectedEvent.end_datetime && ` – ${fmtTime(new Date(selectedEvent.end_datetime))}`}
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-slate-700"><MapPin size={14} className="text-slate-500" /> {selectedEvent.location}</div>
              )}
              {selectedEvent.meeting_link && (
                <a href={selectedEvent.meeting_link} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700">
                  <Video size={14} /> Unirse a la videoconferencia
                </a>
              )}
              {selectedEvent.guests && (
                <div className="flex items-start gap-2 text-slate-700"><Users size={14} className="text-slate-500 mt-0.5" /> <span className="text-xs">{selectedEvent.guests}</span></div>
              )}
              {selectedEvent.description && (
                <p className="text-slate-700 whitespace-pre-wrap">{selectedEvent.description}</p>
              )}
              <div className="flex items-center gap-2 text-slate-500 text-xs"><Bell size={12} /> {selectedEvent.reminder_minutes ?? 30} min antes</div>
              {selectedEvent.priority && (
                <span className="inline-block text-[11px] px-2 py-0.5 rounded-full font-medium"
                      style={{ background: `${PRIORITY_COLORS[selectedEvent.priority]}22`, color: PRIORITY_COLORS[selectedEvent.priority] }}>
                  Prioridad: {PRIORITY_LABELS[selectedEvent.priority]}
                </span>
              )}
              <div className="pt-3 border-t border-slate-200">
                <button onClick={() => setShareModal({ open: true, event: selectedEvent })}
                        className="w-full py-2 bg-slate-100 hover:bg-slate-100 text-slate-700 rounded-lg text-sm flex items-center justify-center gap-2">
                  <Share2 size={14} /> Compartir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Jarvis — lenguaje natural */}
      {jarvisOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4" style={{ zIndex: 99997 }}
             onClick={() => !jarvisLoading && setJarvisOpen(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl max-w-lg w-full p-6 shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div>
                  <h3 className="text-slate-900 font-semibold">Jarvis — Calendario</h3>
                  <p className="text-[11px] text-slate-500">Crea, busca o reorganiza eventos en lenguaje natural</p>
                </div>
              </div>
              <button onClick={() => !jarvisLoading && setJarvisOpen(false)}
                      className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} className="text-slate-500" /></button>
            </div>

            <textarea
              value={jarvisInput}
              onChange={e => setJarvisInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) askJarvis(); }}
              placeholder="Ej: Crea una reunión con Ana mañana a las 3pm en Starbucks Polanco. O: ¿Qué tengo esta semana?"
              autoFocus rows={3}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-indigo-500 resize-none" />

            <div className="flex flex-wrap gap-1.5 mt-2">
              {[
                'Crea cita con dentista mañana 10am',
                'Junta de equipo el viernes 4pm en Zoom',
                '¿Qué tengo hoy?',
                'Recuérdame pagar la luz el día 15 a las 9am',
              ].map((q,i) => (
                <button key={i} onClick={() => setJarvisInput(q)}
                        className="text-[10px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                  {q}
                </button>
              ))}
            </div>

            {jarvisReply && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 whitespace-pre-wrap max-h-60 overflow-y-auto">
                {jarvisReply}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => !jarvisLoading && setJarvisOpen(false)}
                      className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg">
                Cerrar
              </button>
              <button onClick={askJarvis} disabled={jarvisLoading || !jarvisInput.trim()}
                      className="px-5 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                {jarvisLoading ? <><Loader2 size={14} className="animate-spin" /> Pensando…</> : <><Sparkles size={14} /> Enviar</>}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-right">⌘/Ctrl + Enter para enviar</p>
          </div>
        </div>
      )}

      <ShareModal isOpen={shareModal.open} onClose={() => setShareModal({ open: false, event: null })}
        itemType="event" itemId={shareModal.event?.id}
        itemTitle={shareModal.event?.title || 'Evento'}
        itemData={{ userId: user.id, ...shareModal.event }} />

      <AIAgentModule sectionContext="calendario y eventos" compact={true}
        placeholder="Crea, busca o pregúntame sobre tus eventos..." />
    </>
  );
};

export default CalendarModule;
